import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * ModalPortal — escape-hatch wrapper that renders children directly into
 * <body> via React Portal. This sidesteps a CSS gotcha: ancestors with
 * `transform`, `will-change`, `filter`, `perspective`, or `contain` create
 * a containing block that traps `position: fixed` descendants. When that
 * happens, modals stop being viewport-centered and start being centered
 * inside the parent — which is what produces "modal opens far down the
 * page" / "you have to scroll to see the modal" bugs.
 *
 * Use it for all admin modals that aren't already going through Radix
 * Dialog or AdminModal (both of which already portal).
 *
 * Usage:
 *   <ModalPortal>
 *     <AnimatePresence>
 *       {open && <div className="fixed inset-0 ..."> ... </div>}
 *     </AnimatePresence>
 *   </ModalPortal>
 */
const ModalPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted || typeof document === 'undefined') return null;
  return createPortal(children, document.body);
};

export default ModalPortal;
